import { useParams, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { ArrowLeft, ExternalLink, MessageSquare, User, Calendar } from 'lucide-react'
import { toast } from 'sonner'
import { useDefect, useAddComment, useUpdateDefect } from '../api/useDefects'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import type { DefectStatus } from '@/types/defect.types'

const DefectDetailsPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const defectId = parseInt(id!, 10)

  const { data: defect, isLoading } = useDefect(defectId)
  const addComment = useAddComment()
  const updateDefect = useUpdateDefect()

  const [comment, setComment] = useState('')
  const [isEditingStatus, setIsEditingStatus] = useState(false)
  const [newStatus, setNewStatus] = useState<DefectStatus>('open')

  useEffect(() => {
    if (defect?.status) {
      setNewStatus(defect.status as DefectStatus)
    }
  }, [defect?.status])

  // openapi: severity pattern ^(critical|high|medium|low)$
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-500 text-white'
      case 'high': return 'bg-orange-500 text-white'
      case 'medium': return 'bg-yellow-500 text-black'
      case 'low': return 'bg-blue-500 text-white'
      default: return 'bg-gray-500 text-white'
    }
  }

  // openapi: status pattern ^(open|in_progress|resolved|closed|deleted)$
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'bg-blue-500 text-white'
      case 'in_progress': return 'bg-purple-500 text-white'
      case 'resolved': return 'bg-green-500 text-white'
      case 'closed': return 'bg-gray-500 text-white'
      case 'deleted': return 'bg-red-200 text-red-900'
      default: return 'bg-gray-500 text-white'
    }
  }

  // POST /api/defects/{defect_id}/comments
  const handleAddComment = async () => {
    if (!comment.trim()) return
    try {
      await addComment.mutateAsync({
        defectId,
        comment: { comment: comment.trim() },
      })
      setComment('')
      toast.success('Comment added')
    } catch {
      toast.error('Failed to add comment')
    }
  }

  // PUT /api/defects/{defect_id}
  const handleStatusChange = async () => {
    try {
      await updateDefect.mutateAsync({
        id: defectId,
        data: { status: newStatus },
      })
      toast.success('Status updated')
      setIsEditingStatus(false)
    } catch {
      toast.error('Failed to update status')
    }
  }

  if (isLoading) {
    return <div className="p-8 text-center">Loading defect details...</div>
  }

  if (!defect) {
    return <div className="p-8 text-center">Defect not found</div>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/results/defects')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold font-mono">{defect.defect_key}</h1>
              <Badge className={getStatusColor(defect.status)}>
                {defect.status.replace('_', ' ')}
              </Badge>
              <Badge className={getSeverityColor(defect.severity)}>
                {defect.severity}
              </Badge>
              {defect.external_url && (
                <a
                  href={defect.external_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                >
                  <ExternalLink className="h-4 w-4" />
                  View External
                </a>
              )}
            </div>
            <h2 className="text-xl mt-1">{defect.title}</h2>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <Card>
            <CardHeader>
              <CardTitle>Description</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="whitespace-pre-wrap">{defect.description || 'No description provided'}</p>
            </CardContent>
          </Card>

          {/* Reproduction Steps */}
          {defect.steps_to_reproduce && (
            <Card>
              <CardHeader>
                <CardTitle>Steps to Reproduce</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="whitespace-pre-wrap font-sans">{defect.steps_to_reproduce}</pre>
              </CardContent>
            </Card>
          )}

          {/* Expected vs Actual */}
          {(defect.expected_behavior || defect.actual_behavior) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {defect.expected_behavior && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Expected Behavior</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="whitespace-pre-wrap">{defect.expected_behavior}</p>
                  </CardContent>
                </Card>
              )}
              {defect.actual_behavior && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Actual Behavior</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="whitespace-pre-wrap">{defect.actual_behavior}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Comments — POST /api/defects/{id}/comments */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Comments
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {defect.comments && defect.comments.length > 0 ? (
                <div className="space-y-3">
                  {defect.comments.map((c: any) => (
                    <div key={c.id} className="border rounded-lg p-3">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                        <User className="h-3 w-3" />
                        <span>{c.author_name || `User #${c.author_id}`}</span>
                        <span>·</span>
                        <span>{new Date(c.created_at).toLocaleString()}</span>
                        {c.is_internal && (
                          <Badge variant="outline" className="text-xs">Internal</Badge>
                        )}
                      </div>
                      <p className="whitespace-pre-wrap">{c.comment}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground">No comments yet</p>
              )}

              <div className="flex gap-2">
                <Textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Add a comment..."
                  rows={3}
                  className="flex-1"
                />
                <Button
                  onClick={handleAddComment}
                  disabled={!comment.trim() || addComment.isPending}
                  className="self-end"
                >
                  {addComment.isPending ? 'Adding...' : 'Add'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status Management */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Status</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {isEditingStatus ? (
                <div className="space-y-2">
                  {/* openapi: ^(open|in_progress|resolved|closed|deleted)$ */}
                  <Select value={newStatus} onValueChange={(v) => setNewStatus(v as DefectStatus)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleStatusChange} disabled={updateDefect.isPending}>
                      {updateDefect.isPending ? 'Saving...' : 'Save'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setIsEditingStatus(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <Badge className={getStatusColor(defect.status)}>
                    {defect.status.replace('_', ' ')}
                  </Badge>
                  <Button size="sm" variant="outline" onClick={() => setIsEditingStatus(true)}>
                    Change
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-muted-foreground">Priority</Label>
                <div className="mt-1">
                  <Badge className={getSeverityColor(defect.priority)}>
                    {defect.priority}
                  </Badge>
                </div>
              </div>

              <div>
                <Label className="text-muted-foreground">Type</Label>
                <div className="mt-1">{defect.defect_type}</div>
              </div>

              <div>
                <Label className="text-muted-foreground">Project</Label>
                <div className="mt-1">{defect.project_name || 'N/A'}</div>
              </div>

              {defect.environment && (
                <div>
                  <Label className="text-muted-foreground">Environment</Label>
                  <div className="mt-1">{defect.environment}</div>
                </div>
              )}

              {defect.build_version && (
                <div>
                  <Label className="text-muted-foreground">Build Version</Label>
                  <div className="mt-1">{defect.build_version}</div>
                </div>
              )}

              {defect.browser && (
                <div>
                  <Label className="text-muted-foreground">Browser</Label>
                  <div className="mt-1">{defect.browser}</div>
                </div>
              )}

              {defect.os && (
                <div>
                  <Label className="text-muted-foreground">OS</Label>
                  <div className="mt-1">{defect.os}</div>
                </div>
              )}

              <div className="border-t pt-3">
                <Label className="text-muted-foreground">Reported By</Label>
                <div className="mt-1">{defect.reported_by_name || `User #${defect.reported_by}`}</div>
              </div>

              {defect.assigned_to_name && (
                <div>
                  <Label className="text-muted-foreground">Assigned To</Label>
                  <div className="mt-1">{defect.assigned_to_name}</div>
                </div>
              )}

              <div className="border-t pt-3">
                <Label className="text-muted-foreground flex items-center gap-1">
                  <Calendar className="h-3 w-3" /> Created
                </Label>
                <div className="mt-1">{new Date(defect.created_at).toLocaleString()}</div>
              </div>

              {defect.resolved_at && (
                <div>
                  <Label className="text-muted-foreground">Resolved</Label>
                  <div className="mt-1">{new Date(defect.resolved_at).toLocaleString()}</div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tags */}
          {defect.tags && defect.tags.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Tags</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {defect.tags.map((tag: string) => (
                    <Badge key={tag} variant="outline">{tag}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}

export default DefectDetailsPage
